import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Link2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import {
  findDuplicateSource,
  inferSourceType,
  normaliseSourceUrl,
  prepareQuickSource,
  sourceDefaults,
  suggestedSourceName,
} from "@/lib/eventSourceQuick.mjs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("mod_token")}`,
});

const TYPE_OPTIONS = [
  { value: "auto", label: "Automatiskt identifierad" },
  { value: "facebook_page", label: "Facebook-sida" },
  { value: "facebook_group_hint", label: "Facebook-grupp (manuell hänvisning)" },
  { value: "facebook_manual", label: "Facebook-inlägg/evenemang (manuell hänvisning)" },
  { value: "ics", label: "ICS-kalender" },
  { value: "source_watch", label: "Annan offentlig källa (bevakning)" },
];

const TYPE_LABELS = Object.fromEntries(TYPE_OPTIONS.map((item) => [item.value, item.label]));
const MONITORING_LABELS = {
  automated: "automatisk bevakning",
  manual_review: "manuell granskning",
};

function isMaster(user) {
  return user && (user.role === "master" || user.role === "admin");
}

export default function QuickEventSourceCapture({ onSaved }) {
  const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");
  const token = sessionStorage.getItem("mod_token");
  const canEdit = Boolean(token && isMaster(user));

  const [url, setUrl] = useState("");
  const [namn, setNamn] = useState("");
  const [typ, setTyp] = useState("auto");
  const [region, setRegion] = useState("");
  const [kategori, setKategori] = useState("");
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadSources = useCallback(async () => {
    if (!token) return;
    setLoadingSources(true);
    try {
      const { data } = await axios.get(`${API}/mod/event-sources`, {
        headers: modHeaders(),
      });
      setSources(Array.isArray(data) ? data : []);
    } catch {
      setSources([]);
    } finally {
      setLoadingSources(false);
    }
  }, [token]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  const normalisedUrl = useMemo(() => normaliseSourceUrl(url), [url]);
  const inferredType = useMemo(() => inferSourceType(url), [url]);
  const resolvedType = typ === "auto" ? inferredType : typ;
  const duplicate = useMemo(
    () => findDuplicateSource(url, sources),
    [url, sources],
  );
  const defaults = useMemo(() => sourceDefaults(resolvedType), [resolvedType]);
  const suggestedName = useMemo(() => suggestedSourceName(url), [url]);

  if (!canEdit) return null;

  const reset = () => {
    setUrl("");
    setNamn("");
    setTyp("auto");
    setRegion("");
    setKategori("");
    setError("");
  };

  const save = async () => {
    setError("");
    if (!normalisedUrl) {
      setError("Klistra in en giltig URL.");
      return;
    }
    if (duplicate) {
      setError(`Källan finns redan som “${duplicate.namn || duplicate.url}”.`);
      return;
    }

    try {
      prepareQuickSource({ url, namn, typ, region, kategori });
    } catch (err) {
      setError(err?.message || "Källan kunde inte förberedas.");
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/mod/event-sources/quick`,
        { url, namn, typ, region, kategori },
        { headers: modHeaders() },
      );
      toast.success("Källan tillagd", {
        description: `${TYPE_LABELS[data.typ] || data.typ} · ${MONITORING_LABELS[data.monitoring_mode] || data.monitoring_mode || data.trust}`,
      });
      reset();
      await loadSources();
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "duplicate_source") {
        const existing = detail.existing || {};
        setError(`Källan finns redan som “${existing.namn || existing.url || "befintlig källa"}”.`);
      } else {
        setError(typeof detail === "string" ? detail : "Källan kunde inte sparas.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="quick-event-source-capture"
      className="max-w-5xl mx-auto px-4 sm:px-6 pt-8"
    >
      <div className="bg-white border border-warm-border rounded-sm shadow-sm p-4 sm:p-5">
        <div className="flex items-start gap-3 mb-4">
          <Link2 size={20} className="text-pine shrink-0 mt-0.5" />
          <div>
            <h2 className="font-heading text-lg font-medium text-ink">Snabbregistrera källa</h2>
            <p className="text-sm text-bark mt-1 max-w-3xl">
              Klistra in en Facebook- eller annan käll-URL. Källan läggs direkt i aktiv bevakning.
              Offentliga Facebook-sidor kan bevakas automatiskt; grupper och enskilda inlägg kräver manuell granskning och hämtas inte automatiskt.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)] gap-3">
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="quick-source-url">
              URL
            </label>
            <input
              id="quick-source-url"
              data-testid="quick-source-url"
              type="text"
              inputMode="url"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                setError("");
              }}
              placeholder="https://www.facebook.com/..."
              className="w-full border border-warm-border bg-white px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="quick-source-type">
              Källtyp
            </label>
            <select
              id="quick-source-type"
              data-testid="quick-source-type"
              value={typ}
              onChange={(event) => setTyp(event.target.value)}
              className="w-full border border-warm-border bg-white px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
            >
              {TYPE_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="quick-source-name">
              Namn <span className="font-normal text-muted-foreground">(kan lämnas tomt)</span>
            </label>
            <input
              id="quick-source-name"
              data-testid="quick-source-name"
              type="text"
              value={namn}
              onChange={(event) => setNamn(event.target.value)}
              placeholder={suggestedName || "Källans namn"}
              className="w-full border border-warm-border bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="quick-source-region">
              Region
            </label>
            <input
              id="quick-source-region"
              type="text"
              value={region}
              onChange={(event) => setRegion(event.target.value)}
              placeholder="t.ex. Backe"
              className="w-full border border-warm-border bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="quick-source-category">
              Kategori
            </label>
            <input
              id="quick-source-category"
              type="text"
              value={kategori}
              onChange={(event) => setKategori(event.target.value)}
              placeholder="t.ex. Föreningar"
              className="w-full border border-warm-border bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
            />
          </div>
        </div>

        {normalisedUrl && (
          <div className={`mt-4 border rounded-sm p-3 text-sm ${
            duplicate ? "border-falu/40 bg-falu/5" : "border-pine/30 bg-pine/5"
          }`}>
            <div className="flex items-start gap-2">
              {duplicate ? (
                <AlertTriangle size={16} className="text-falu shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 size={16} className="text-pine shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <p className={duplicate ? "text-falu font-medium" : "text-pine font-medium"}>
                  {duplicate
                    ? `Dubblett: ${duplicate.namn || duplicate.url}`
                    : "Ingen dubblett hittad"}
                </p>
                <p className="text-xs text-bark mt-1 break-all">{normalisedUrl}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Typ: {TYPE_LABELS[resolvedType] || resolvedType} · {defaults.active ? "aktiv" : "inaktiv"} · {MONITORING_LABELS[defaults.monitoring_mode] || defaults.monitoring_mode} · trust: {defaults.trust}
                  {loadingSources ? " · uppdaterar källista…" : ""}
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div data-testid="quick-source-error" className="mt-3 flex items-start gap-2 text-sm text-falu">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            data-testid="quick-source-save"
            onClick={save}
            disabled={saving || loadingSources || !normalisedUrl || Boolean(duplicate)}
            className="inline-flex items-center gap-2 bg-pine hover:bg-pine-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            {saving ? "Sparar…" : "Lägg till källa"}
          </button>
        </div>
      </div>
    </section>
  );
}
